import type { ServiceDto } from '@fos/shared/contracts/services';
import { SERVICE_KIND_LABELS, SERVICE_STATUS_LABELS, SERVICE_STATUS_TONES } from '@fos/shared/labels';
import { Wrench } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatDate } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { servicePriceText } from '../service-format';
import { ServiceFormDialog } from './ServiceFormDialog';

/**
 * Lista usług klientów (zakładka „Usługi” na liście Projektów: `setProjectsServicesTab(ServicesListTab)`;
 * Figma 07 — widok bez osobnego projektu, wzorzec tabeli Projektów). Wiersz: nazwa i klient, rodzaj, cena
 * z cyklem (tylko z „Widzi stawki i kwoty”), następna płatność, status. Klik: karta zasobu umowy utrzymania
 * albo formularz usługi. Nową usługę dodaje się w profilu klienta.
 */
export function ServicesListTab() {
  const access = useAccess();
  const navigate = useNavigate();
  const enabled = access.can('projects') || access.can('maintenance');
  const query = trpc.services.list.useQuery({}, { enabled });
  const [editing, setEditing] = useState<ServiceDto | null>(null);
  if (!enabled) return null;
  if (query.isPending) return <ListSkeleton rows={4} />;
  if (query.isError) {
    return <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />;
  }
  if (query.data.length === 0) {
    return (
      <EmptyState
        icon={Wrench}
        title="Brak usług"
        description="Umowy utrzymania, konsultacje i pakiety godzin dodasz w profilu klienta."
      />
    );
  }
  const seesRates = access.flag('seesRates');
  const open = (s: ServiceDto) => {
    const asset = s.kind === 'maintenance' && access.can('maintenance') ? s.assets[0] : undefined;
    if (asset) navigate(`/utrzymanie/${asset.id}`);
    else if (access.can(s.kind === 'maintenance' ? 'maintenance' : 'projects', 'edit')) setEditing(s);
  };
  const th = 'h-9 px-1 text-caption font-medium text-fg-muted';
  return (
    <>
      <table className="w-full table-fixed border-collapse text-left text-body-s">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className={th}>
              Usługa
            </th>
            <th scope="col" className={cn(th, 'hidden w-[140px] md:table-cell')}>
              Rodzaj
            </th>
            {seesRates && (
              <th scope="col" className={cn(th, 'hidden w-[150px] lg:table-cell')}>
                Cena
              </th>
            )}
            <th scope="col" className={cn(th, 'hidden w-[140px] sm:table-cell')}>
              Następna płatność
            </th>
            <th scope="col" className={cn(th, 'w-[110px]')}>
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {query.data.map((s) => (
            <tr
              key={s.id}
              onClick={() => open(s)}
              className="cursor-pointer border-b border-line transition-colors hover:bg-surface"
            >
              <td className="h-[60px] px-1 pr-4">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden
                    style={s.client.color ? ({ backgroundColor: s.client.color } as CSSProperties) : undefined}
                    className={cn('inline-block size-2 shrink-0 rounded-full', !s.client.color && 'bg-line-strong')}
                  />
                  <div className="flex min-w-0 flex-col">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        open(s);
                      }}
                      className="truncate rounded-xs text-left font-semibold text-fg focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      {s.name}
                    </button>
                    <span className="truncate text-caption text-fg-muted">{s.client.name}</span>
                  </div>
                </div>
              </td>
              <td className="hidden px-1 text-fg-secondary md:table-cell">{SERVICE_KIND_LABELS[s.kind]}</td>
              {seesRates && (
                <td className="hidden px-1 text-fg-secondary lg:table-cell">{servicePriceText(s) ?? '—'}</td>
              )}
              <td
                className={cn(
                  'hidden px-1 sm:table-cell',
                  s.daysToPayment !== null && s.daysToPayment < 0 ? 'text-danger' : 'text-fg-secondary',
                )}
              >
                {s.nextPaymentDate ? formatDate(s.nextPaymentDate) : '—'}
              </td>
              <td className="px-1">
                <StatusPill tone={SERVICE_STATUS_TONES[s.status]}>{SERVICE_STATUS_LABELS[s.status]}</StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <ServiceFormDialog
          open
          onOpenChange={(o) => !o && setEditing(null)}
          clientId={editing.clientId}
          service={editing}
        />
      )}
    </>
  );
}
